/**
 * Floor-plan and map colour tokens (`skin.map`).
 *
 * Platform defaults reproduce the colours the map components hard-code today
 * (docs/requirements/design-system-import.md §5.3), so a tenant without map
 * tokens renders unchanged. Tenant values cascade per §5.4 and are emitted as
 * `--map-*` CSS variables; `resolvedMapColours()` returns the same values as
 * plain hex colours for Three.js and Leaflet, which cannot read CSS classes.
 */
import type { CustomerSkinEffective, CustomerSkinTheme } from '@cdf/api-client-react';
import { readableForeground, type SkinThemeName } from './customer-skin';

/** One platform default: the CSS value the stylesheet declares and its plain sRGB value. */
export interface MapTokenDefault {
  /** Value declared on `:root` in index.css. */
  css: string;
  /** Plain hex for non-CSS consumers; null when the value follows a theme token. */
  hex: string | null;
  /** Where today's value comes from: a Tailwind palette step, a component literal or a theme token. */
  source: string;
}

const tw = (step: string, css: string, hex: string): MapTokenDefault => ({ css, hex, source: `tailwind:${step}` });
const literal = (hex: string): MapTokenDefault => ({ css: hex, hex, source: 'literal' });
const themed = (css: string): MapTokenDefault => ({ css, hex: null, source: 'theme' });

const EMERALD_500 = tw('emerald-500', 'oklch(69.6% 0.17 162.48)', '#10b981');
const EMERALD_600 = tw('emerald-600', 'oklch(59.6% 0.145 163.225)', '#059669');
const EMERALD_700 = tw('emerald-700', 'oklch(50.8% 0.118 165.612)', '#047857');
const SLATE_100 = tw('slate-100', 'oklch(96.8% 0.007 247.896)', '#f1f5f9');
const SLATE_300 = tw('slate-300', 'oklch(86.9% 0.022 252.894)', '#cbd5e1');
const SLATE_500 = tw('slate-500', 'oklch(55.4% 0.046 257.417)', '#64748b');
const SLATE_600 = tw('slate-600', 'oklch(44.6% 0.043 257.281)', '#475569');
const SLATE_950 = tw('slate-950', 'oklch(12.9% 0.042 264.695)', '#020617');
const GRAY_500 = tw('gray-500', 'oklch(55.1% 0.027 264.364)', '#6b7280');
const GRAY_600 = tw('gray-600', 'oklch(44.6% 0.03 256.802)', '#4b5563');
const ROSE_500 = tw('rose-500', 'oklch(64.5% 0.246 16.439)', '#f43f5e');
const ROSE_600 = tw('rose-600', 'oklch(58.6% 0.253 17.585)', '#e11d48');
const RED_600 = tw('red-600', 'oklch(57.7% 0.245 27.325)', '#dc2626');
const RED_700 = tw('red-700', 'oklch(50.5% 0.213 27.518)', '#b91c1c');
const AMBER_400 = tw('amber-400', 'oklch(82.8% 0.189 84.429)', '#fbbf24');
const AMBER_600 = tw('amber-600', 'oklch(66.6% 0.179 58.318)', '#d97706');
const GREEN_600 = tw('green-600', 'oklch(62.7% 0.194 149.214)', '#16a34a');
const VIOLET_600 = tw('violet-600', 'oklch(54.1% 0.281 293.009)', '#7c3aed');
const VIOLET_700 = tw('violet-700', 'oklch(49.1% 0.27 292.581)', '#6d28d9');
const WHITE = tw('white', '#fff', '#ffffff');

type SpaceLeaf = 'fill' | 'stroke' | 'foreground' | 'areaFill' | 'areaStroke';

function spaceState<S extends string>(fill: MapTokenDefault, stroke: MapTokenDefault, foreground: MapTokenDefault,
  areaFill: MapTokenDefault, areaStroke: MapTokenDefault, state: S): Record<`space.${S}.${SpaceLeaf}`, MapTokenDefault> {
  // Computed template keys widen to string in TypeScript; the record type restores the literal paths.
  return {
    [`space.${state}.fill`]: fill, [`space.${state}.stroke`]: stroke, [`space.${state}.foreground`]: foreground,
    [`space.${state}.areaFill`]: areaFill, [`space.${state}.areaStroke`]: areaStroke
  } as Record<`space.${S}.${SpaceLeaf}`, MapTokenDefault>;
}

/** Today's hard-coded map colours, keyed by token path (§5.3). */
export const MAP_PLATFORM_DEFAULTS = {
  ...spaceState(EMERALD_600, EMERALD_700, WHITE, EMERALD_600, EMERALD_700, 'available'),
  ...spaceState(EMERALD_600, EMERALD_700, WHITE, EMERALD_600, EMERALD_700, 'selected'),
  ...spaceState(EMERALD_600, EMERALD_700, WHITE, EMERALD_600, EMERALD_700, 'considering'),
  'space.considering.ring': SLATE_950,
  ...spaceState(SLATE_100, SLATE_600, SLATE_950, SLATE_300, SLATE_500, 'booked'),
  'space.booked.teamStroke': SLATE_950,
  'space.mine.fill': themed('var(--customer-accent, var(--primary))'),
  'space.mine.stroke': themed('var(--customer-accent, var(--primary))'),
  'space.mine.foreground': themed('var(--customer-accent-foreground, var(--primary-foreground))'),
  'space.teammate.fill': VIOLET_600,
  'space.teammate.stroke': VIOLET_700,
  'space.teammate.foreground': WHITE,
  ...spaceState(GRAY_500, GRAY_600, WHITE, GRAY_500, GRAY_600, 'unavailable'),
  ...spaceState(themed('var(--muted)'), themed('var(--border)'), themed('var(--muted-foreground)'),
    themed('var(--muted)'), themed('var(--border)'), 'unknown'),
  'space.confirmed.fill': { css: 'oklch(0.95 0.04 155)', hex: '#daf7e3', source: 'shared-space-strip.css' },
  'space.confirmed.foreground': { css: 'oklch(0.38 0.09 155)', hex: '#07502c', source: 'shared-space-strip.css' },
  'tone.available.fill': EMERALD_500,
  'tone.available.stroke': EMERALD_600,
  'tone.busy.fill': ROSE_500,
  'tone.busy.stroke': ROSE_600,
  'tone.neutral.fill': SLATE_500,
  'tone.neutral.stroke': SLATE_600,
  'presence.available': EMERALD_600,
  'presence.busy': RED_600,
  'presence.away': AMBER_400,
  'presence.doNotDisturb': RED_700,
  'presence.offline': SLATE_500,
  'presence.awayMark': SLATE_950,
  'presence.foreground': WHITE,
  'capacity.good': GREEN_600,
  'capacity.limited': AMBER_600,
  'capacity.full': RED_600,
  'capacity.foreground': WHITE,
  'campus.available': literal('#059669'),
  'campus.availableSelected': literal('#047857'),
  'campus.full': literal('#78716c'),
  'campus.fullSelected': literal('#b45309'),
  'campus.unknown': literal('#334155'),
  'campus.unknownSelected': literal('#0f766e'),
  'campus.pinForeground': literal('#ffffff'),
  'campus.pinBorder': literal('#ffffff'),
  'campus.selectedRing': literal('#0f766e'),
  'scene.background': literal('#f4f7f9'),
  'scene.floor': literal('#d9e1e5'),
  'scene.label': literal('#0f172a'),
  'scene.labelForeground': literal('#f8fafc'),
  'scene.available': literal('#23a67a'),
  'scene.busy': literal('#d65d57'),
  'scene.neutral': literal('#6d8fa7'),
  'scene.highlight': literal('#f2a93b'),
  'scene.marker': literal('#9b59b6')
} satisfies Record<string, MapTokenDefault>;

export type MapTokenPath = keyof typeof MAP_PLATFORM_DEFAULTS;
export const mapTokenPaths = Object.keys(MAP_PLATFORM_DEFAULTS) as MapTokenPath[];

/** `space.available.areaFill` → `--map-space-available-area-fill`. */
export function mapCssVariable(path: MapTokenPath): string {
  return '--map-' + path.split('.').map(part => part.replace(/[A-Z]/g, letter => '-' + letter.toLowerCase())).join('-');
}

type ThemeRef = `theme:${'success' | 'onSuccess'}`;
type MapSource = MapTokenPath | ThemeRef;

const fallbackStates = ['available', 'selected', 'considering', 'booked', 'unavailable', 'unknown'] as const;

/** §5.4 cascade: a missing tenant leaf takes the first tenant-resolved source. */
const MAP_CASCADE: Partial<Record<MapTokenPath, readonly MapSource[]>> = {
  ...Object.fromEntries(fallbackStates.flatMap(state => {
    const follows = state === 'selected' || state === 'considering';
    return [
      [`space.${state}.areaFill`, [`space.${state}.fill`, ...(follows ? ['space.available.areaFill'] : [])]],
      [`space.${state}.areaStroke`, [`space.${state}.stroke`, ...(follows ? ['space.available.areaStroke'] : [])]],
      ...(follows ? (['fill', 'stroke', 'foreground'] as const).map(leaf => [`space.${state}.${leaf}`, [`space.available.${leaf}`]]) : [])
    ];
  })),
  // The teammate double border follows the tenant's teammate outline, then its booked outline.
  'space.booked.teamStroke': ['space.teammate.stroke', 'space.booked.stroke'],
  'space.confirmed.fill': ['theme:success'],
  'space.confirmed.foreground': ['theme:onSuccess'],
  'tone.available.fill': ['space.available.fill'],
  'tone.available.stroke': ['space.available.stroke'],
  'tone.busy.fill': ['space.booked.areaStroke'],
  'tone.busy.stroke': ['space.booked.areaStroke'],
  'tone.neutral.fill': ['space.unknown.areaFill'],
  'tone.neutral.stroke': ['space.unknown.areaStroke'],
  'capacity.good': ['space.available.fill'],
  'campus.available': ['capacity.good'],
  'campus.availableSelected': ['space.available.stroke'],
  'campus.full': ['space.unavailable.fill'],
  'campus.fullSelected': ['space.unavailable.stroke'],
  'campus.unknown': ['space.unknown.stroke'],
  'campus.unknownSelected': ['space.unknown.stroke'],
  'scene.available': ['space.available.fill'],
  'scene.busy': ['space.booked.areaStroke'],
  'scene.neutral': ['space.unknown.stroke'],
  'scene.highlight': ['space.selected.stroke']
};

/** Space-state foregrounds derived from a tenant fill when none is supplied. */
const DERIVED_FOREGROUNDS: Partial<Record<MapTokenPath, MapTokenPath>> = {
  'space.available.foreground': 'space.available.fill',
  'space.selected.foreground': 'space.selected.fill',
  'space.considering.foreground': 'space.considering.fill',
  'space.booked.foreground': 'space.booked.fill',
  'space.mine.foreground': 'space.mine.fill',
  'space.teammate.foreground': 'space.teammate.fill',
  'space.unavailable.foreground': 'space.unavailable.fill',
  'space.unknown.foreground': 'space.unknown.fill',
  'space.confirmed.foreground': 'space.confirmed.fill'
};

const hexPattern = /^#[0-9a-fA-F]{6}$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function leafAt(source: unknown, path: string): string | null {
  let value: unknown = source;
  for (const part of path.split('.')) value = isRecord(value) ? value[part] : undefined;
  return typeof value === 'string' && hexPattern.test(value) ? value : null;
}

export type SkinMapInput = Pick<CustomerSkinEffective, 'map' | 'applyScope'> | null | undefined;

/** True unless the administrator switched floor plans and maps off. */
export function mapsInScope(skin: SkinMapInput): boolean {
  return skin?.applyScope?.maps !== false;
}

/**
 * Tenant-resolved map colours for one mode: explicit values plus the §5.4
 * cascade, never platform defaults. Empty when the maps scope is off.
 */
export function tenantMapTokens(
  skin: SkinMapInput,
  theme: SkinThemeName,
  themeValues: CustomerSkinTheme = {}
): Partial<Record<MapTokenPath, string>> {
  if (!mapsInScope(skin)) return {};
  const mode = theme === 'dark' ? skin?.map?.dark : skin?.map?.light;
  const resolved: Partial<Record<MapTokenPath, string>> = {};
  const visiting = new Set<MapTokenPath>();
  const lookup = (path: MapTokenPath): string | null => {
    if (path in resolved) return resolved[path] ?? null;
    if (visiting.has(path)) return null;
    visiting.add(path);
    let value = leafAt(mode, path);
    for (const source of MAP_CASCADE[path] ?? []) {
      if (value) break;
      value = source.startsWith('theme:')
        ? leafAt(themeValues, source.slice('theme:'.length))
        : lookup(source as MapTokenPath);
    }
    const derivedFrom = DERIVED_FOREGROUNDS[path];
    if (!value && derivedFrom) {
      const fill = lookup(derivedFrom);
      if (fill) value = readableForeground(fill);
    }
    visiting.delete(path);
    if (value) resolved[path] = value;
    return value;
  };
  for (const path of mapTokenPaths) lookup(path);
  return resolved;
}

/** `--map-*` CSS variables for the tenant-resolved values; absent leaves keep the stylesheet defaults. */
export function mapTokenVariables(
  skin: SkinMapInput,
  theme: SkinThemeName,
  themeValues: CustomerSkinTheme = {}
): Record<string, string> {
  const tokens: Record<string, string> = {};
  const tenant = tenantMapTokens(skin, theme, themeValues);
  for (const [path, value] of Object.entries(tenant) as Array<[MapTokenPath, string]>) {
    tokens[mapCssVariable(path)] = value;
  }
  for (const [variable, path] of Object.entries(TEAM_BOOKING_VARIABLES)) {
    const value = tenant[path];
    if (value) tokens[variable] = value;
  }
  return tokens;
}

/**
 * Simplified-map teammate bookings (booked badges and areas with the double border)
 * use the tenant's "Your teammate's" colours. These variables are only emitted when
 * the tenant sets those colours; otherwise the components fall back to the booked
 * variables, so tenants without teammate colours keep today's look.
 */
export const TEAM_BOOKING_VARIABLES = {
  '--map-space-booked-team-fill': 'space.teammate.fill',
  '--map-space-booked-team-foreground': 'space.teammate.foreground',
  '--map-space-booked-team-area-fill': 'space.teammate.fill',
  '--map-space-booked-team-area-stroke': 'space.teammate.stroke'
} as const satisfies Record<string, MapTokenPath>;

/** sRGB equivalents of the app's neutral tokens, for theme-following defaults outside CSS. */
const APP_THEME_HEX: Record<SkinThemeName, { primary: string; onPrimary: string; muted: string; border: string; onMuted: string }> = {
  light: { primary: '#171717', onPrimary: '#fafafa', muted: '#f5f5f5', border: '#e5e5e5', onMuted: '#737373' },
  dark: { primary: '#e5e5e5', onPrimary: '#171717', muted: '#262626', border: '#222222', onMuted: '#a1a1a1' }
};

type StatePair = { fill: string; stroke: string };
type SpaceColours = StatePair & { foreground: string; areaFill: string; areaStroke: string };

/** Every map colour as a plain value, for Three.js, Leaflet and other non-CSS consumers. */
export interface ResolvedMapColours {
  space: {
    available: SpaceColours;
    selected: SpaceColours;
    considering: SpaceColours & { ring: string };
    booked: SpaceColours & { teamStroke: string };
    mine: StatePair & { foreground: string };
    teammate: StatePair & { foreground: string };
    unavailable: SpaceColours;
    unknown: SpaceColours;
    confirmed: { fill: string; foreground: string };
  };
  tone: { available: StatePair; busy: StatePair; neutral: StatePair };
  presence: { available: string; busy: string; away: string; doNotDisturb: string; offline: string; awayMark: string; foreground: string };
  capacity: { good: string; limited: string; full: string; foreground: string };
  campus: {
    available: string; availableSelected: string; full: string; fullSelected: string; unknown: string;
    unknownSelected: string; pinForeground: string; pinBorder: string; selectedRing: string;
  };
  scene: {
    background: string; floor: string; label: string; labelForeground: string; available: string;
    busy: string; neutral: string; highlight: string; marker: string;
  };
}

/**
 * Resolved map colours for one mode: tenant values when the maps scope is on,
 * otherwise (and for every absent leaf) the platform defaults. Theme-following
 * defaults use the skin's accent/primary and muted/border pairs, like the CSS.
 */
export function resolvedMapColours(
  skin: (SkinMapInput & Partial<Pick<CustomerSkinEffective, 'light' | 'dark'>>) | null | undefined,
  theme: SkinThemeName
): ResolvedMapColours {
  const themeValues: CustomerSkinTheme = (theme === 'dark' ? skin?.dark : skin?.light) ?? {};
  const tenant = tenantMapTokens(skin, theme, themeValues);
  const app = APP_THEME_HEX[theme];
  const mineFill = themeValues.accent ?? themeValues.primary ?? app.primary;
  const themedHex: Partial<Record<MapTokenPath, string>> = {
    'space.mine.fill': mineFill,
    'space.mine.stroke': mineFill,
    'space.mine.foreground': themeValues.accent || themeValues.primary
      ? (themeValues.accent ? readableForeground(themeValues.accent) : themeValues.onPrimary ?? readableForeground(mineFill)) ?? app.onPrimary
      : app.onPrimary,
    'space.unknown.fill': themeValues.muted ?? app.muted,
    'space.unknown.areaFill': themeValues.muted ?? app.muted,
    'space.unknown.stroke': themeValues.border ?? app.border,
    'space.unknown.areaStroke': themeValues.border ?? app.border,
    'space.unknown.foreground': themeValues.onMuted ?? app.onMuted
  };
  const get = (path: MapTokenPath): string =>
    tenant[path] ?? MAP_PLATFORM_DEFAULTS[path].hex ?? themedHex[path] ?? app.primary;
  const space = (state: 'available' | 'selected' | 'considering' | 'booked' | 'unavailable' | 'unknown'): SpaceColours => ({
    fill: get(`space.${state}.fill`), stroke: get(`space.${state}.stroke`), foreground: get(`space.${state}.foreground`),
    areaFill: get(`space.${state}.areaFill`), areaStroke: get(`space.${state}.areaStroke`)
  });
  const pair = (tone: 'available' | 'busy' | 'neutral'): StatePair => ({
    fill: get(`tone.${tone}.fill`), stroke: get(`tone.${tone}.stroke`)
  });
  return {
    space: {
      available: space('available'),
      selected: space('selected'),
      considering: { ...space('considering'), ring: get('space.considering.ring') },
      booked: { ...space('booked'), teamStroke: get('space.booked.teamStroke') },
      mine: { fill: get('space.mine.fill'), stroke: get('space.mine.stroke'), foreground: get('space.mine.foreground') },
      teammate: { fill: get('space.teammate.fill'), stroke: get('space.teammate.stroke'), foreground: get('space.teammate.foreground') },
      unavailable: space('unavailable'),
      unknown: space('unknown'),
      confirmed: { fill: get('space.confirmed.fill'), foreground: get('space.confirmed.foreground') }
    },
    tone: { available: pair('available'), busy: pair('busy'), neutral: pair('neutral') },
    presence: {
      available: get('presence.available'), busy: get('presence.busy'), away: get('presence.away'),
      doNotDisturb: get('presence.doNotDisturb'), offline: get('presence.offline'),
      awayMark: get('presence.awayMark'), foreground: get('presence.foreground')
    },
    capacity: {
      good: get('capacity.good'), limited: get('capacity.limited'), full: get('capacity.full'), foreground: get('capacity.foreground')
    },
    campus: {
      available: get('campus.available'), availableSelected: get('campus.availableSelected'), full: get('campus.full'),
      fullSelected: get('campus.fullSelected'), unknown: get('campus.unknown'), unknownSelected: get('campus.unknownSelected'),
      pinForeground: get('campus.pinForeground'), pinBorder: get('campus.pinBorder'), selectedRing: get('campus.selectedRing')
    },
    scene: {
      background: get('scene.background'), floor: get('scene.floor'), label: get('scene.label'),
      labelForeground: get('scene.labelForeground'), available: get('scene.available'), busy: get('scene.busy'),
      neutral: get('scene.neutral'), highlight: get('scene.highlight'), marker: get('scene.marker')
    }
  };
}
